/**
 * Public roadmap data — rendered by `routes/roadmap.tsx` and
 * linked from the landing page.
 *
 * Four lanes:
 *   - shipped      — live in production, available to customers today
 *   - building     — active work, likely within the next ~1 month
 *   - next         — committed, not started yet, ~1–3 months out
 *   - later        — directional, subject to change based on usage + feedback
 *
 * IMPORTANT: This is a **customer-facing** page, not an engineering
 * changelog. Descriptions must read in plain language. Avoid:
 *   - Framework names (Liveblocks, Zustand, Tone.js, Capacitor internals)
 *   - Store / AWS / API internals (S3, DynamoDB, OfflineAudioContext)
 *   - Wave / PR / sprint numbers
 *   - CRDT / webhook / entitlement jargon
 *   - Route paths beyond the obvious (`/werkshop/tools/sampler` is fine
 *     to mention as "the Sampler tool"; `/werkshop/settings` is not
 *     useful copy for a customer)
 *
 * Say what the user can DO, not how it's built. Examples:
 *   ❌ "Liveblocks JWT-mint endpoint + typed client"
 *   ✅ "Live presence cursors when you share a project"
 *   ❌ "Offline Tone.js render pipeline"
 *   ✅ "Export full tracks to WAV"
 *
 * When something ships, move it from `building` (or `next`) to
 * `shipped` — don't delete items; the record of progress is part
 * of the trust signal.
 */

export type RoadmapLane = 'shipped' | 'building' | 'next' | 'later'

/** Short tag chip rendered next to the item title. */
export type RoadmapApp =
  | 'Bussboard'
  | 'Werkshop'
  | 'Sequencer'
  | 'Sampler'
  | 'MIDI'
  | 'Piano Roll'
  | 'Automation'
  | 'Inspector'
  | 'Store'
  | 'Multiplayer'
  | 'Platform'
  | 'Native'

export interface RoadmapItem {
  title: string
  description: string
  app?: RoadmapApp
  /** Approximate completion/landing date, free-form string. */
  when?: string
}

export const ROADMAP: Record<RoadmapLane, RoadmapItem[]> = {
  shipped: [
    {
      title: 'Bussboard (node-canvas app)',
      description:
        'Map any signal-flow rig — studio, live stage, broadcast truck, camera + lighting — on a drag-and-drop canvas. 85+ device presets, zones, shareable links, and AI-assisted routing suggestions.',
      app: 'Bussboard',
      when: 'Apr 2026',
    },
    {
      title: 'Werkshop — the browser DAW',
      description:
        'One DAW that nests a step sequencer, MIDI router, sampler, creative synths, piano roll, automation lanes, and a module store. Your open project follows you across every surface.',
      app: 'Werkshop',
      when: 'Apr 2026',
    },
    {
      title: 'Step sequencer',
      description:
        'Pad grid with step editor, five layout modes (Bars / Tracks / Grid / Quads / Tracker), four built-in kits, a master FX bank, song mode, and MIDI-in pad triggering. Per-step velocity, pitch, FX sends, and slice locks.',
      app: 'Sequencer',
      when: 'Apr 2026',
    },
    {
      title: 'Piano Roll',
      description:
        'Draw per-pad notes at sub-step resolution. Click to add, drag to move, drag edges to resize. Plays back with a moving cursor that auto-scrolls. Pops out full-screen as its own tool.',
      app: 'Piano Roll',
      when: 'Apr 2026',
    },
    {
      title: 'Automation lanes',
      description:
        'Shape parameters over time with breakpoint curves — filter cutoff, resonance, pitch, and every parameter on your pads. Duplicate lanes, set min/max ranges, clear-all. Inline tab or full-screen editor.',
      app: 'Automation',
      when: 'Apr 2026',
    },
    {
      title: 'Sampler with Slicer',
      description:
        'First-party sampler that drops onto any pad. Auto-BPM detect, beat-aligned slicing, trim, normalize, lo-fi, and time-stretching. Full-screen editor for deep sample work, or drop samples straight into a pad and go.',
      app: 'Sampler',
      when: 'Apr 2026',
    },
    {
      title: 'Creative synths — Deep Sub, Pluck Pad, Drone',
      description:
        'Three built-in synths you can drop onto any pad from the pad-editor picker. Every parameter is automatable and editable from the Inspector with the rest of your instruments.',
      app: 'Werkshop',
      when: 'Apr 2026',
    },
    {
      title: 'Inspector dock',
      description:
        'Ableton-style channel strip at the bottom of the sequencer. Click a pad to see its instrument params, a bus to see its FX chain, or nothing to edit project-wide settings. Press "I" to hide / show.',
      app: 'Inspector',
      when: 'Apr 2026',
    },
    {
      title: 'MIDI Router',
      description:
        'Patchbay-style browser MIDI routing with drag-to-connect cables. Your virtual keyboard and sequencer act as routable sources; any MIDI output shows up as a destination.',
      app: 'MIDI',
      when: 'Apr 2026',
    },
    {
      title: 'Module Store — per-module licensing',
      description:
        'The Sequencer and MIDI Router are free forever. Every other module — Sampler, Deep Sub, Pluck Pad, Drone, Piano Roll, Automation — can be unlocked individually at a Personal or Commercial price. Buy only what you use.',
      app: 'Store',
      when: 'Apr 2026',
    },
    {
      title: 'WAV export',
      description:
        'Render your full project to a WAV file with a live progress indicator. Master-bus FX are baked into the export automatically.',
      app: 'Sequencer',
      when: 'Apr 2026',
    },
    {
      title: 'Project sync + sample uploads',
      description:
        'Cloud-synced projects, drag-and-drop sample uploads, and smart sample preloading so playback never stutters waiting on downloads.',
      app: 'Werkshop',
      when: 'Apr 2026',
    },
    {
      title: 'Shared presence (scaffolding)',
      description:
        'Groundwork for live collaboration — a shared room for each Bussboard environment and Werkshop project. Presence cursors turn on in the next update.',
      app: 'Multiplayer',
      when: 'Apr 2026',
    },
  ],
  building: [
    {
      title: 'Checkout for modules',
      description:
        'Per-module Personal + Commercial purchase flow with monthly, annual, and one-time pricing. Already-licensed modules show as owned in the Store; cancellation honours the full paid period.',
      app: 'Store',
    },
    {
      title: 'Simpler plan structure',
      description:
        "Retiring the legacy Free / Pro / Studio tier ladder now that every module is individually priced. Existing subscribers keep equivalent access — no one loses features they're already using.",
      app: 'Platform',
    },
    {
      title: 'More device presets',
      description:
        'Expanding the preset library: more mastering and outboard classics (API, Neve, SSL), modular Eurorack staples, broadcast gear (RE20, Rode PodMic), and iOS-compatible interfaces.',
      app: 'Bussboard',
    },
    {
      title: 'Live presence cursors',
      description:
        'Flipping on real-time cursors and focus highlights in shared Bussboard environments and Werkshop projects, so collaborators can see each other move around.',
      app: 'Multiplayer',
    },
  ],
  next: [
    {
      title: 'Native plugin hosting (iOS AUv3 + macOS VST3)',
      description:
        "On iPad/iPhone and macOS, Busswerks will host third-party AUv3, VST3, and AU plugins you've installed — drop them onto pads alongside the built-in synths. Web keeps every first-party module unchanged.",
      app: 'Native',
    },
    {
      title: 'Switchboard-styled canvas',
      description:
        'An optional patchbay skin for the Bussboard canvas: jack-plate device nodes, cable-style connections with a natural sag, and a rack-panel background. Opt-in — the clean theme stays the default.',
      app: 'Bussboard',
    },
    {
      title: 'Automation on buses',
      description:
        'Extend automation lanes to bus-level parameters — reverb wet, delay time, bus filter cutoff — in addition to pad parameters.',
      app: 'Automation',
    },
    {
      title: 'Real-time collaborative editing',
      description:
        'Beyond presence: live co-editing of your Bussboard environment and sequencer project. Move devices, toggle steps, and tweak params with collaborators in the same room.',
      app: 'Multiplayer',
    },
    {
      title: 'Full-fidelity granular + multisample export',
      description:
        'Granular playback and multisample zones render bit-for-bit in WAV export, matching what you hear live.',
      app: 'Sequencer',
    },
    {
      title: 'Arrangement view + song mode',
      description:
        'Chain projects into a full song with per-project repeats, mutes, and scene changes, with a timeline arrangement view to edit the whole structure at once.',
      app: 'Sequencer',
    },
  ],
  later: [
    {
      title: 'Mastering / outboard app',
      description:
        'A third top-level app: gain-staging calculators, impedance matching helpers, and a mastering reference chain builder. Keeps the one-workspace story honest as the catalog grows.',
      app: 'Platform',
    },
    {
      title: 'Higher-fidelity built-in effects',
      description:
        'Upgrade the bitcrush, stutter, and other master-FX with higher-fidelity DSP under the hood — same controls, cleaner sound.',
      app: 'Sequencer',
    },
    {
      title: 'Community preset sharing',
      description:
        'Publish your own Bussboard device presets and use presets others have shared, with a moderation queue and verified badges.',
      app: 'Bussboard',
    },
    {
      title: 'Team-shared sample library',
      description:
        'Commercial-tier seats unlock a pooled sample library — upload once, every seat on the account sees it.',
      app: 'Werkshop',
    },
    {
      title: 'Linux native plugin hosting',
      description:
        'For Linux users: a native build with LV2 plugin hosting to match the iOS / macOS story. Web already gives Linux users every first-party Werkshop module; this adds third-party plugins on top.',
      app: 'Native',
    },
  ],
}

export const LANE_LABELS: Record<RoadmapLane, string> = {
  shipped: 'Shipped',
  building: 'Building now',
  next: 'Next',
  later: 'Later',
}

export const LANE_DESCRIPTIONS: Record<RoadmapLane, string> = {
  shipped:
    'Live in production today. Your subscription works on these right now.',
  building: 'Active work. Expect these within roughly the next month.',
  next: 'Committed \u2014 starts after "Building now" ships. Rough 1\u20133 month window.',
  later: 'Directional. These land if usage and feedback keep pushing us there.',
}
